import type { Master } from "@/api/masterService";
import { formattedDateTime } from "@/utils/date";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import BorderColorIcon from "@mui/icons-material/BorderColor";

interface MasterTableProps {
  data: Array<Master>;
  isLoading: boolean;
  handleEdit: (row: Master) => () => void;
  canEdit?: boolean;
}

const MasterTable: React.FC<MasterTableProps> = ({
  data,
  isLoading,
  handleEdit,
  canEdit,
}) => {
  const columns: GridColDef<Master>[] = [
    {
      field: "name",
      headerName: "ชื่อ",
      minWidth: 100,
      flex: 0.2,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>{param.row.name}</Typography>
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "คำอธิบาย",
      minWidth: 150,
      flex: 0.25,
      headerClassName: "custom-header",
    },
    {
      field: "is_active",
      headerName: "สถานะ",
      width: 100,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Chip
            label={param.row.is_active ? "Active" : "Inactive"}
            sx={{
              backgroundColor: param.row.is_active
                ? "success.light"
                : "error.light",
              color: "#fff",
              fontWeight: "600",
              fontSize: "0.8rem",
              width: "80px",
            }}
            key={param.row.id}
          />
        </Box>
      ),
    },
    {
      field: "created_by",
      headerName: "Created By",
      minWidth: 100,
      flex: 0.1,
      headerClassName: "custom-header",
      valueGetter: (_value, row) => `${row.created_by || "-"}`,
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 100,
      flex: 0.15,
      headerClassName: "custom-header",
      valueGetter: (_value, row) =>
        `${formattedDateTime(row.created_at) || "-"}`,
    },
    {
      field: "updated_by",
      headerName: "Updated By",
      minWidth: 100,
      flex: 0.1,
      headerClassName: "custom-header",
      valueGetter: (_value, row) => `${row.updated_by || "-"}`,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      minWidth: 100,
      flex: 0.15,
      headerClassName: "custom-header",
      valueGetter: (_value, row) =>
        `${formattedDateTime(row.updated_at) || "-"}`,
    },
    {
      field: "Action",
      headerName: "Action",
      width: 100,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Tooltip
            title={"แก้ไข"}
            placement="top"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
            }}
          >
            <IconButton disabled={!canEdit} onClick={handleEdit(param.row)}>
              <BorderColorIcon color={canEdit ? "primary" : "disabled"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
        }}
      />
    </Box>
  );
};

export default MasterTable;
